// What the signed-in person (or the person an administrator is viewing as) can open. Loaded once, shared by the
// navigation, the launcher, the route guard and the Security pages; reloaded when View as changes or a role changes.
import { useSyncExternalStore } from "react";
import { request } from "../api";
import { SUITE, areaForDepartment, areaNav, type Domain } from "../suite";

export type Grant = { read: boolean; create: boolean; edit: boolean; delete: boolean };
export type SecMe = {
  enforced: boolean; admin: boolean; realAdmin: boolean; blocked: boolean; everything: boolean;
  roles: string[]; implicitDefault: boolean; apps: Record<string, Grant>;
  viewAs: { id: string; name: string; readOnly: boolean } | null;
  person: { id: string; name: string; title: string | null; department: string | null; regions: string[] };
  screens: { app: string; paths: string[] }[];
};

let me: SecMe | null = null;
let loading = false;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export async function reloadAccess() {
  loading = true;
  try { me = await request<SecMe>("GET", "/api/security/me"); } catch { /* signed out or offline: show everything; the API still enforces */ }
  loading = false; emit();
}

export function useAccess(): SecMe | null {
  return useSyncExternalStore((f) => { subs.add(f); if (!me && !loading) void reloadAccess(); return () => subs.delete(f); }, () => me);
}

/** The app a screen belongs to, if Security governs it. */
export function appForRoute(m: SecMe, path: string): string | null {
  let best: { app: string; len: number } | null = null;
  for (const s of m.screens) for (const p of s.paths) {
    if (path === p || path.startsWith(p.endsWith("/") ? p : p + "/") || path.startsWith(p + "?")) if (!best || p.length > best.len) best = { app: s.app, len: p.length };
  }
  return best?.app ?? null;
}

/** Can this person open a screen? Unknown screens and simple apps (/m/:id) follow their app; anything ungoverned is open. */
export function canOpen(m: SecMe | null, path: string): boolean {
  if (!m || m.everything) return true;
  if (path.startsWith("/settings/security")) return m.realAdmin;
  const simple = path.match(/^\/m\/([^/?]+)/)?.[1];
  const app = simple ?? appForRoute(m, path);
  return !app || !!m.apps[app]?.read;
}

export const can = (m: SecMe | null, app: string, action: keyof Grant) => !m || m.everything || !!m.apps[app]?.[action];

export function startViewAs(p: { id: string; name: string }) {
  sessionStorage.setItem("viewAs", JSON.stringify(p));
  forgetLanding(); // so it opens on their start page, as it would for them
  location.assign("/");
}
export function stopViewAs(to = "/settings/security") {
  sessionStorage.removeItem("viewAs");
  location.assign(to);
}

/** An area shows when it has an app the person can open, or when nothing in it is governed (links, BC pages). */
export function areaOpen(m: SecMe | null, areaId: string): boolean {
  if (!m || m.everything) return true;
  const d = SUITE.find((x) => x.id === areaId);
  if (!d) return true;
  if (areaId === "admin") return m.realAdmin || canOpen(m, "/settings/links");
  const pages = areaNav(d).filter((p) => p.to && !p.to.startsWith("/a/"));
  const governed = pages.filter((p) => !canOpen({ ...m, apps: {} }, p.to!.split("?")[0]));
  if (!governed.length) return true;
  return pages.some((p) => canOpen(m, p.to!.split("?")[0]));
}

/** The area a person starts in: the one their department names (Sales → CRM), when they can open it. People whose
 *  department spans areas, or who have none, start on Courage home. */
export function startArea(m: SecMe | null): Domain | null {
  const d = areaForDepartment(m?.person?.department);
  return d && areaOpen(m, d.id) ? d : null;
}

// Landing happens once per visit (per browser tab): opening courageMFG at its root goes to the person's start area.
// After that, the logo and every link to / show Courage home.
const LANDED = "courage.landed";
export const hasLanded = () => { try { return sessionStorage.getItem(LANDED) === "1"; } catch { return true; } };
export const markLanded = () => { try { sessionStorage.setItem(LANDED, "1"); } catch { /* private window */ } };
export const forgetLanding = () => { try { sessionStorage.removeItem(LANDED); } catch { /* private window */ } };
